import type { LiveJob, HistoryResponse, DateRange, BatchRun, BatchHistoryResponse, ScanImagesResponse } from '../types';
import { createLogger } from '../utils/logger';
import { apiFetch } from '../utils/api';

const log = createLogger('useJobs');

/**
 * Fetch live jobs (pending/processing)
 */
export async function fetchLiveJobs(): Promise<LiveJob[]> {
  log.debug('Fetching live jobs');
  const response = await apiFetch('/api/jobs/live');
  if (!response.ok) {
    log.warn(`Failed to fetch live jobs: ${response.status} ${response.statusText}`);
    throw new Error(`Failed to fetch live jobs: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Kill a single job by ID
 */
export async function killJob(jobId: string): Promise<{ message: string; job_id: string; scan_id: string; status: string }> {
  log.debug(`Killing job ${jobId}`);
  const response = await apiFetch(`/api/jobs/${jobId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    const error = await response.text();
    log.error(`Failed to kill job ${jobId}: ${response.status}`, error);
    throw new Error(error || `Failed to kill job: ${response.statusText}`);
  }
  const result = await response.json();
  log.info(`Killed job ${jobId}`);
  return result;
}

/**
 * Kill all live jobs (pending/processing)
 */
export async function killAllJobs(): Promise<{ message: string; killed_count: number }> {
  log.debug('Killing all live jobs');
  const response = await apiFetch('/api/jobs/live', {
    method: 'DELETE',
  });
  if (!response.ok) {
    const error = await response.text();
    log.error(`Failed to kill all jobs: ${response.status}`, error);
    throw new Error(error || `Failed to kill all jobs: ${response.statusText}`);
  }
  const result = await response.json();
  log.info(`Killed all jobs: ${result.killed_count} cancelled`);
  return result;
}

/**
 * Re-run a finished job by creating a new job with the same scan_id
 */
export async function rerunJob(jobId: string): Promise<{ job_id: string; scan_id: string; message: string }> {
  log.debug(`Re-running job ${jobId}`);
  const response = await apiFetch(`/api/jobs/${jobId}/rerun`, {
    method: 'POST',
  });
  if (!response.ok) {
    const error = await response.text();
    log.error(`Failed to re-run job ${jobId}: ${response.status}`, error);
    throw new Error(error || `Failed to re-run job: ${response.statusText}`);
  }
  const result = await response.json();
  log.info(`Re-ran job ${jobId}, new job: ${result.job_id}`);
  return result;
}

/**
 * Fetch presigned image URLs for the generated images of a completed scan.
 */
export async function fetchScanImages(jobId: string): Promise<ScanImagesResponse> {
  log.debug(`Fetching scan images for job ${jobId}`);
  const response = await apiFetch(`/api/jobs/${jobId}/images`);
  if (!response.ok) {
    const error = await response.text();
    log.warn(`Failed to fetch scan images for ${jobId}: ${response.status}`, error);
    throw new Error(error || `Failed to fetch scan images: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Delete a historical job permanently
 */
export async function deleteHistoricalJob(jobId: string): Promise<{ message: string; job_id: string }> {
  log.debug(`Deleting historical job ${jobId}`);
  const response = await apiFetch(`/api/jobs/history/${jobId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    const error = await response.text();
    log.error(`Failed to delete job ${jobId}: ${response.status}`, error);
    throw new Error(error || `Failed to delete job: ${response.statusText}`);
  }
  const result = await response.json();
  log.info(`Deleted historical job ${jobId}`);
  return result;
}

/**
 * Fetch historical jobs with filters
 */
export async function fetchHistoricalJobs(
  dateRange: DateRange,
  status: string | null,
  page: number,
  pageSize: number,
  scanId?: string | null,
  organizationId?: string | null,
): Promise<HistoryResponse> {
  const params = new URLSearchParams();
  
  if (dateRange.from) {
    params.set('from_date', dateRange.from.toISOString());
  }
  if (dateRange.to) {
    params.set('to_date', dateRange.to.toISOString());
  }
  if (status) {
    params.set('status', status);
  }
  if (scanId) {
    params.set('scan_id', scanId);
  }
  if (organizationId) {
    params.set('organization_id', organizationId);
  }
  params.set('page', page.toString());
  params.set('page_size', pageSize.toString());

  log.debug(`Fetching historical jobs: page=${page}, status=${status}`);
  const response = await apiFetch(`/api/jobs/history?${params.toString()}`);
  if (!response.ok) {
    log.warn(`Failed to fetch history: ${response.status} ${response.statusText}`);
    throw new Error(`Failed to fetch history: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Export jobs as CSV or JSON
 */
export async function exportJobs(
  dateRange: DateRange,
  status: string | null,
  format: 'csv' | 'json'
): Promise<void> {
  const params = new URLSearchParams();
  
  if (dateRange.from) {
    params.set('from_date', dateRange.from.toISOString());
  }
  if (dateRange.to) {
    params.set('to_date', dateRange.to.toISOString());
  }
  if (status) {
    params.set('status', status);
  }
  params.set('format', format);

  log.debug(`Exporting jobs as ${format}`);
  const response = await apiFetch(`/api/jobs/export?${params.toString()}`);
  if (!response.ok) {
    log.error(`Failed to export jobs: ${response.status} ${response.statusText}`);
    throw new Error(`Failed to export: ${response.statusText}`);
  }
  log.info(`Exported jobs as ${format}`);

  // Get filename from Content-Disposition header or use default
  const contentDisposition = response.headers.get('Content-Disposition');
  let filename = `jobs_export.${format}`;
  if (contentDisposition) {
    const match = contentDisposition.match(/filename=(.+)/);
    if (match) {
      filename = match[1];
    }
  }

  // Download the file
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Fetch active batch runs with progress
 */
export async function fetchBatchRuns(): Promise<BatchRun[]> {
  log.debug('Fetching batch runs');
  const response = await apiFetch('/api/batches');
  if (!response.ok) {
    log.warn(`Failed to fetch batch runs: ${response.status} ${response.statusText}`);
    throw new Error(`Failed to fetch batch runs: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Create a new batch of scans
 */
export async function createBatch(
  filename: string,
  scanIds: string[]
): Promise<{ batch_id: string; filename: string; total_scans: number; message: string }> {
  log.debug(`Creating batch: filename=${filename}, scans=${scanIds.length}`);
  const response = await apiFetch('/api/batches', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ filename, scan_ids: scanIds }),
  });
  
  if (!response.ok) {
    const error = await response.text();
    log.error(`Failed to create batch: ${response.status}`, error);
    throw new Error(error || 'Failed to create batch');
  }
  
  const result = await response.json();
  log.info(`Created batch ${result.batch_id}: ${scanIds.length} scans`);
  return result;
}

/**
 * Fetch completed batch history with filters
 */
export async function fetchBatchHistory(
  dateRange: DateRange,
  page: number,
  pageSize: number
): Promise<BatchHistoryResponse> {
  const params = new URLSearchParams();
  
  if (dateRange.from) {
    params.set('from_date', dateRange.from.toISOString());
  }
  if (dateRange.to) {
    params.set('to_date', dateRange.to.toISOString());
  }
  params.set('page', page.toString());
  params.set('page_size', pageSize.toString());

  log.debug(`Fetching batch history: page=${page}`);
  const response = await apiFetch(`/api/batches/history?${params.toString()}`);
  if (!response.ok) {
    log.warn(`Failed to fetch batch history: ${response.status} ${response.statusText}`);
    throw new Error(`Failed to fetch batch history: ${response.statusText}`);
  }
  return response.json();
}
